// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import { TokenManagerFactory } from '@protolabs/enterprise-token-manager';
import tokenManagerConfig from '@/tokenManagerConfig';

TokenManagerFactory.getOrCreateTokenManager(tokenManagerConfig);

import { createApp, h as createElement } from 'vue';
import i18n, { loadDefaultLanguageAsync } from '@/i18n';

import dfmServiceWrapper from '@/utils/dfmServiceWrapper';

import commerceComp from '@/views/Commerce.vue';
import LoadingPlaceholder from '@/components/LoadingPlaceholder.vue';
import store from '@/store/guest';
import router from '@/router/guest';
import dialogsPlugin from '@/dialogs';
import componentLibrary from '@protolabs/component-library';
import { actionTypes as addressActionTypes } from '@/store/modules/addressing';
import { useGeolocationStore } from '@/store/geolocation';
import { actionTypes as featureActionTypes } from '@/store/modules/features';
import { actionTypes as tasActionTypes } from '@/store/modules/tasDocuments';
import { useCommerceStore } from '@/store/commerce';
import { createPiniaStore } from '@/store/pinia';
import TechnicalAssetUpload from '@protolabs/technical-asset-upload';
import CatalogComponentLibrary from '@protolabs/catalog-component-library';
import '@protolabs/catalog-component-library/dist/style.css';

export const loadApp = () =>
	loadDefaultLanguageAsync(navigator.language).then((language) => {
		const createVue = () => {
			const pinia = createPiniaStore(true, window.isDemoQuote);

			const vueApp = createApp({
				components: { commerceComp },
				render: () => createElement(commerceComp),
				setup() {
					const { updateLocaleForGuestMode } = useCommerceStore();
					const { loadCountries, loadLinkGroups, loadLanguageLinks } =
						useGeolocationStore();

					return {
						updateLocaleForGuestMode,
						loadCountries,
						loadLinkGroups,
						loadLanguageLinks,
					};
				},
				beforeCreate() {
					this.$store.dispatch(addressActionTypes.GET_LOGIN_COUNTRY_CODE);
					this.loadLanguageLinks();
					this.loadCountries();
					this.loadLanguageLinks();
					this.updateLocaleForGuestMode(language);
					this.$store.dispatch(tasActionTypes.LOAD_DOC_TYPES);
				},
			})
				.use(componentLibrary, {
					theme: 'protolabs-2023',
				})
				.use(i18n)
				.use(store)
				.use(router)
				.use(pinia)
				.use(dialogsPlugin, {
					theme: 'protolabs-2023',
				})
				.use(TechnicalAssetUpload)
				.use(CatalogComponentLibrary)
				.component('loading-placeholder-comp', LoadingPlaceholder);

			vueApp.config.globalProperties.$guest = true;
			vueApp.config.globalProperties.$demoQuote = window.isDemoQuote;

			dfmServiceWrapper.initialize();

			vueApp.directive('hide', function (el, binding) {
				el.style.visibility = binding.value ? 'hidden' : 'visible';
			});

			vueApp.mount('#commerce');
		};

		store.dispatch(featureActionTypes.LOAD_FEATURES).then(() => createVue());
	});
